/*
  Who can see which devices.

  This used to be decided by `userData['custom:bucket'].startsWith("Triton")` in
  Dashboard.js: if your bucket NAME happened to begin with "Triton" you were
  treated as a fleet-wide admin and the dashboard scanned all of MIRA_TB. That is
  not an admin check, it is a string coincidence -- the shared bucket for
  slugmaa/Eevee is literally named `TritonBio-cc0021bf...`, so every ordinary user
  of that bucket silently got access to all ~40 devices (found 2026-07-27).

  Admin is now the real signal: membership of the `super-admins` Cognito group,
  which is the same group NotifyUser and the provisioning toolkit already treat as
  fleet-wide. Everyone else is scoped to their own group's devices.

  NOTE: this governs what the dashboard LISTS. Storage-level isolation is
  enforced by the Cognito identity-pool IAM role, which is a separate control.
*/

export const SUPER_ADMIN_GROUP = 'super-admins';

/** True if the user's Cognito groups grant fleet-wide access. */
export function isSuperAdmin(groups) {
  if (!Array.isArray(groups)) return false;
  return groups.includes(SUPER_ADMIN_GROUP);
}

/** Pull `cognito:groups` out of an Amplify v6 auth session, defensively. */
export function groupsFromSession(session) {
  const claim = session?.tokens?.idToken?.payload?.['cognito:groups'];
  if (Array.isArray(claim)) return claim;
  if (typeof claim === 'string') return [claim];   // single-group tokens
  return [];
}

/**
 * DynamoDB scan params for the device list.
 * Super-admins scan the whole table; everyone else is filtered to the devices
 * carrying their own MIRA_group_id.
 */
export function deviceScanParams({ groups, userBucket }) {
  if (isSuperAdmin(groups)) {
    return { TableName: 'MIRA_TB' };
  }
  return {
    TableName: 'MIRA_TB',
    FilterExpression: '#key = :value',
    ExpressionAttributeNames: { '#key': 'MIRA_group_id' },
    // A missing bucket must scope to nothing, never to everything.
    ExpressionAttributeValues: { ':value': `${userBucket ?? ''}_users` },
  };
}

/**
 * Collapse the device list to one entry per physical device.
 *
 * A device is registered once per access group, keyed by
 * (MIRA_group_id, MIRA_device_id), so a unit shared with two groups is two rows
 * pointing at one `device_bucket`. A scoped user only ever matches one of them,
 * but a super-admin scans the whole table and sees the same device twice
 * (Ponyta, 2026-09-21: rows under `PonytaMIRA-..._users` and
 * `TritonBio-..._users`, identical bucket, identical data).
 *
 * De-duplicating on `device_bucket` is what makes that one entry: the bucket is
 * the device's storage identity, so two rows sharing it are the same hardware
 * no matter how the ids are cased (`ponyta` vs `Ponyta`).
 *
 * Which row wins matters, because the row carries the device's certificate. The
 * one whose `MIRA_device_id` matches an IoT thing with a live certificate is the
 * usable record; the other is a leftover registration. That cannot be checked
 * from the browser, so the tie-break is the closest available proxy: prefer the
 * row whose group is derived from the device's own bucket, which is the one
 * provisioning creates alongside the thing. Rows with no bucket cannot be
 * compared and are passed through untouched rather than silently dropped.
 */
export function dedupeDevices(items) {
  if (!Array.isArray(items)) return [];
  const byBucket = new Map();
  const passthrough = [];
  for (const item of items) {
    const bucket = item?.device_bucket;
    if (!bucket) { passthrough.push(item); continue; }
    const seen = byBucket.get(bucket);
    if (!seen) { byBucket.set(bucket, item); continue; }
    if (preferDeviceRow(item, seen, bucket) === item) byBucket.set(bucket, item);
  }
  return [...byBucket.values(), ...passthrough];
}

/** The row more likely to be the live registration for `bucket`. */
export function preferDeviceRow(a, b, bucket) {
  const own = (r) => r?.MIRA_group_id === `${bucket}_users`;
  if (own(a) !== own(b)) return own(a) ? a : b;
  return b;                                   // no signal: keep the first seen
}
